import { useQuery } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { isEmpty } from '@newfront-insurance/core';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMarkChatAsRead } from '../../../hooks';
import type { NovaSettingsData, NovaSettingField } from '../../../hooks/use-nova-settings';
import { GET_CHATS_BY_FILTERS, type ChatAppType } from '../../../queries/chats';
import { browserNotificationManager } from '../../../services/browser-notification-manager';
import { isChatActivelyViewed, subscribeActiveChatViews } from '../lib/active-chat-views';
import { ACTIVE_CHAT_STATUS } from '../lib/chat-optimistic-updates';
import {
  getLastLocalSendAtMs,
  noteLocalSend as noteLocalSendSignal,
  subscribeLocalSendSignal,
} from '../lib/local-send-signal';
import { evaluateChatsEmission, isTabAndWindowVisible } from '../lib/notification-evaluator';
import { normalizeChatUuid } from '../lib/notification-helpers';
import { claimNotifiedWatermark, getNotifiedWatermark, releaseNotifiedWatermark } from '../lib/notification-watermarks';

/** Poll cadence while a stream is (or may be) running somewhere. */
const ACTIVE_STREAM_POLL_INTERVAL_MS = 10_000;

/**
 * Baseline poll cadence. This is what lets a tab that never observed a stream
 * still surface unread state and notifications (e.g. the run started in
 * another tab, or completed during a reload).
 */
const BASELINE_POLL_INTERVAL_MS = 60_000;

/**
 * How long a local send keeps fast polling alive while the server may not yet
 * report the stream on the chat row (brand-new chats). Once the row carries an
 * activeStreamId, that sustains fast polling by itself.
 */
const LOCAL_SEND_FAST_POLL_WINDOW_MS = 180_000;

/** Focus/visibility events can fire together; don't refetch twice for one return. */
const FOCUS_REFETCH_THROTTLE_MS = 1_000;

export interface ChatNotificationController {
  // State
  hasNotifications: boolean;
  browserNotificationsEnabled: boolean;
  browserNotificationPermission: NotificationPermission;
  browserNotificationsSupported: boolean;

  // Actions
  enableBrowserNotifications(): Promise<boolean>;
  disableBrowserNotifications(): void;

  /**
   * Records that this tab just submitted a chat request, sustaining fast
   * polling until the server lists the stream on the chat row. Tab-wide:
   * works from any provider, not just the notification owner.
   */
  noteLocalSend(): void;

  /**
   * Called by stream lifecycle finish callbacks. If the finished chat is on
   * screen in a focused tab RIGHT NOW (event-time check — safe for orphaned
   * callbacks, since a switched-away chat is no longer registered), the turn
   * counts as seen: its watermark is claimed and the chat marked read before
   * the refetch, so the post-completion emission can't toast for an answer
   * the user just watched arrive.
   */
  noteStreamFinish(chatUuid: string): void;

  /**
   * Fire-and-forget refresh of the chats list. Safe to call from any stream
   * callback — refreshing server state is the only effect.
   */
  refetchChats(): void;
}

/**
 * Owns unread badges and browser notifications for chats.
 *
 * The model is level-triggered reconciliation over server-owned state: every
 * chats-list emission — and every change to the rule's non-data inputs
 * (visibility, on-screen chats, permission) — is evaluated against one
 * idempotent rule (see notification-evaluator.ts), deduped by a persistent
 * per-chat watermark. Nothing here detects events, diffs snapshots, or trusts
 * component lifecycle; display gates (feature flag, server setting,
 * permission) are read live at decision time, never copied into longer-lived
 * state.
 */
export function useChatNotificationController({
  userUuid,
  effectiveAppType,
  serverSettings,
  onPersist,
  isPublicMode = false,
  isNotificationOwner = true,
  onNotificationClick,
}: {
  userUuid?: string;
  effectiveAppType?: ChatAppType | null;
  serverSettings?: NovaSettingsData | null;
  onPersist?: (field: NovaSettingField, value: unknown) => void;
  isPublicMode?: boolean;
  /**
   * Exactly one provider per tab should own polling, evaluation, and display.
   * Secondary embeds pass false: they still read unread state from the shared
   * cache, and the chats they have on screen are excluded from notifications
   * tab-wide via the active-chat-views registry.
   */
  isNotificationOwner?: boolean;
  /** Invoked with the normalized chat uuid when a notification is clicked. */
  onNotificationClick?: (chatUuid: string) => void;
}): ChatNotificationController {
  const isNovaNotificationsEnabled = useFeatureFlag('nova-notifications', false);
  const [browserNotificationPermission, setBrowserNotificationPermission] = useState<NotificationPermission>(() =>
    browserNotificationManager.getPermission(),
  );
  // Bumped whenever a non-data input of the evaluation rule changes
  // (focus/visibility, on-screen chats, local sends) so the reconciler
  // re-runs even when the chats data itself is referentially unchanged.
  const [evaluationTick, setEvaluationTick] = useState(0);
  const sessionStartedAtRef = useRef<number>(Date.now());
  const markReadInFlightRef = useRef<Set<string>>(new Set());
  const lastFocusRefetchAtRef = useRef<number>(0);
  const markChatAsRead = useMarkChatAsRead();

  // Held in a ref so an unstable parent callback can't churn the evaluator
  // effect; the latest handler is read at display time.
  const onNotificationClickRef = useRef(onNotificationClick);
  onNotificationClickRef.current = onNotificationClick;

  const isActive = !isPublicMode && !isEmpty(userUuid);

  const { data, refetch, startPolling, stopPolling } = useQuery(GET_CHATS_BY_FILTERS, {
    variables: {
      userUuid,
      appType: effectiveAppType,
      statusFilter: ACTIVE_CHAT_STATUS,
    },
    skip: !isActive,
    fetchPolicy: 'cache-and-network',
  });

  const { chatsList: chats = [] } = data ?? {};

  const refetchChats = useCallback(() => {
    if (!isActive) {
      return;
    }
    refetch().catch((error: unknown) => {
      browserLogger.debug('[Notification Controller] Chats refetch failed', { error });
    });
  }, [isActive, refetch]);

  const bumpEvaluationTick = useCallback(() => {
    setEvaluationTick((tick) => tick + 1);
  }, []);

  const noteLocalSend = useCallback(() => {
    noteLocalSendSignal(Date.now());
  }, []);

  // Tab-wide signals: re-evaluate (and recompute the poll interval) when the
  // set of on-screen chats changes or any provider submits a request.
  useEffect(() => {
    const unsubscribeViews = subscribeActiveChatViews(bumpEvaluationTick);
    const unsubscribeSends = subscribeLocalSendSignal(bumpEvaluationTick);
    return () => {
      unsubscribeViews();
      unsubscribeSends();
    };
  }, [bumpEvaluationTick]);

  // A local send only needs to sustain fast polling until the server reports
  // the stream; re-render when the window expires instead of tracking the
  // handoff explicitly.
  const lastLocalSendAtMs = getLastLocalSendAtMs();
  const isAwaitingLocalSend =
    lastLocalSendAtMs !== null && Date.now() - lastLocalSendAtMs < LOCAL_SEND_FAST_POLL_WINDOW_MS;
  useEffect(() => {
    if (!isAwaitingLocalSend || lastLocalSendAtMs === null) {
      return undefined;
    }
    const remaining = LOCAL_SEND_FAST_POLL_WINDOW_MS - (Date.now() - lastLocalSendAtMs);
    const timer = window.setTimeout(bumpEvaluationTick, Math.max(remaining, 0));
    return () => window.clearTimeout(timer);
  }, [isAwaitingLocalSend, lastLocalSendAtMs, bumpEvaluationTick]);

  // The owner polls: fast while anything is (or may be) streaming, baseline
  // otherwise so completions observed by no mounted chat still surface.
  const hasActiveStreams = chats.some((chat) => chat.activeStreamId);
  const pollIntervalMs =
    hasActiveStreams || isAwaitingLocalSend ? ACTIVE_STREAM_POLL_INTERVAL_MS : BASELINE_POLL_INTERVAL_MS;
  useEffect(() => {
    if (!isNotificationOwner || !isActive) {
      return undefined;
    }

    startPolling(pollIntervalMs);
    return () => stopPolling();
  }, [isNotificationOwner, isActive, pollIntervalMs, startPolling, stopPolling]);

  // Returning to the tab refreshes immediately instead of waiting out the
  // poll interval (background tabs also throttle timers), and re-evaluates —
  // focus alone can change the rule's outcome (markRead instead of badge).
  useEffect(() => {
    if (!isNotificationOwner || !isActive) {
      return undefined;
    }

    const handleVisible = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      bumpEvaluationTick();
      const now = Date.now();
      if (now - lastFocusRefetchAtRef.current >= FOCUS_REFETCH_THROTTLE_MS) {
        lastFocusRefetchAtRef.current = now;
        refetchChats();
      }
    };

    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('focus', handleVisible);
    return () => {
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('focus', handleVisible);
    };
  }, [isNotificationOwner, isActive, refetchChats, bumpEvaluationTick]);

  // Keep the settings UI honest when permission changes outside our flow
  // (e.g. via browser site settings).
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
      return undefined;
    }

    let permissionStatus: PermissionStatus | undefined;
    let cancelled = false;

    navigator.permissions
      .query({ name: 'notifications' as PermissionName })
      .then((status) => {
        if (cancelled) {
          return;
        }
        permissionStatus = status;
        status.onchange = () => setBrowserNotificationPermission(browserNotificationManager.getPermission());
      })
      .catch(() => {
        // Permissions API unavailable for notifications — the state still
        // refreshes after our own requestPermission flow.
      });

    return () => {
      cancelled = true;
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, []);

  const notificationsEnabled = serverSettings?.notificationsEnabled === true;

  const markChatReadGuarded = useCallback(
    (chatUuid: string) => {
      if (markReadInFlightRef.current.has(chatUuid)) {
        return;
      }
      markReadInFlightRef.current.add(chatUuid);
      void markChatAsRead(chatUuid).finally(() => {
        markReadInFlightRef.current.delete(chatUuid);
      });
    },
    [markChatAsRead],
  );

  const noteStreamFinish = useCallback(
    (chatUuid: string) => {
      const normalizedChatUuid = normalizeChatUuid(chatUuid);
      if (isActive && isChatActivelyViewed(normalizedChatUuid) && isTabAndWindowVisible()) {
        // The user watched this turn complete on screen — suppress the toast
        // before the post-completion emission can race them leaving. Claiming
        // with the client clock is approximate (Stage 2 keys watermarks on the
        // server's assistant timestamp); markRead is the durable suppression.
        claimNotifiedWatermark(normalizedChatUuid, Date.now());
        markChatReadGuarded(normalizedChatUuid);
      }
      refetchChats();
    },
    [isActive, markChatReadGuarded, refetchChats],
  );

  // The reconciler: one idempotent pass per chats emission or rule-input change.
  useEffect(() => {
    if (!isNotificationOwner || !isActive || chats.length === 0) {
      return;
    }

    const actions = evaluateChatsEmission({
      chats,
      isTabVisible: isTabAndWindowVisible(),
      sessionStartedAtMs: sessionStartedAtRef.current,
      nowMs: Date.now(),
      getWatermark: getNotifiedWatermark,
      isChatActivelyViewed,
    });

    for (const { chatUuid, lastMessageAtMs } of actions.markRead) {
      // Record the watermark first so switching away later cannot resurface
      // a toast for a message the user already saw on screen.
      claimNotifiedWatermark(chatUuid, lastMessageAtMs);
      markChatReadGuarded(chatUuid);
    }

    for (const candidate of actions.notify) {
      // Gates are sampled here, live, on purpose: a flag or setting that
      // arrives late simply applies from the next evaluation. Skipping
      // without claiming the watermark preserves delivery for when gates open.
      if (!isNovaNotificationsEnabled || !notificationsEnabled || !browserNotificationManager.hasPermission()) {
        browserLogger.debug('[Notification Controller] Notification gated off', {
          chatUuid: candidate.chatUuid,
          flagEnabled: isNovaNotificationsEnabled,
          settingEnabled: notificationsEnabled,
          permission: browserNotificationManager.getPermission(),
        });
        continue;
      }

      if (!claimNotifiedWatermark(candidate.chatUuid, candidate.lastMessageAtMs)) {
        continue;
      }

      const shown = browserNotificationManager.showChatNotification({
        body: candidate.body,
        chatUuid: candidate.chatUuid,
        onClick: (clickedChatUuid) => onNotificationClickRef.current?.(clickedChatUuid),
      });

      if (shown) {
        browserLogger.info('[Notification Controller] Notification shown', {
          chatUuid: candidate.chatUuid,
          lastMessageAtMs: candidate.lastMessageAtMs,
        });
      } else {
        // Display failed after the claim — roll it back so the next emission
        // can retry instead of silently consuming this chat's delivery.
        releaseNotifiedWatermark(candidate.chatUuid, candidate.lastMessageAtMs);
      }
    }
  }, [
    chats,
    evaluationTick,
    isNotificationOwner,
    isActive,
    isNovaNotificationsEnabled,
    notificationsEnabled,
    browserNotificationPermission,
    markChatReadGuarded,
  ]);

  const hasNotifications = useMemo(() => chats.some((chat) => chat.hasUnreadMessages), [chats]);

  const enableBrowserNotifications = useCallback(async (): Promise<boolean> => {
    const permission = await browserNotificationManager.requestPermission();
    setBrowserNotificationPermission(permission);
    if (permission !== 'granted') {
      return false;
    }
    onPersist?.('notificationsEnabled', true);
    return true;
  }, [onPersist]);

  const disableBrowserNotifications = useCallback(() => {
    onPersist?.('notificationsEnabled', false);
  }, [onPersist]);

  // The toggle reflects reality: enabled means the server-side preference is
  // on AND this browser can actually display notifications.
  const browserNotificationsEnabled = notificationsEnabled && browserNotificationPermission === 'granted';

  return useMemo(
    () => ({
      hasNotifications,
      browserNotificationsEnabled,
      browserNotificationPermission,
      browserNotificationsSupported: browserNotificationManager.isSupported(),
      enableBrowserNotifications,
      disableBrowserNotifications,
      noteLocalSend,
      noteStreamFinish,
      refetchChats,
    }),
    [
      hasNotifications,
      browserNotificationsEnabled,
      browserNotificationPermission,
      enableBrowserNotifications,
      disableBrowserNotifications,
      noteLocalSend,
      noteStreamFinish,
      refetchChats,
    ],
  );
}
